import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { GetStartedDialog } from '@/components/landing/GetStartedDialog';

const CANONICAL = 'https://culturelens.org/guides/measuring-organizational-culture';
const TITLE = 'How to Measure Organizational Culture: A Guide for Mid-Market Leaders';
const DESCRIPTION =
  'A practical guide to measuring organizational culture in mid-market and PE-backed companies — moving past generic surveys to close the gap between stated and lived culture.';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: TITLE,
  description: DESCRIPTION,
  mainEntityOfPage: CANONICAL,
  author: { '@type': 'Organization', name: 'CultureLens' },
  publisher: { '@type': 'Organization', name: 'CultureLens' },
  inLanguage: 'en',
};

export default function MeasuringOrganizationalCulture() {
  // Lightweight per-page head without adding react-helmet-async as a dependency.
  if (typeof document !== 'undefined') {
    document.title = `${TITLE} | CultureLens`;
    const setMeta = (selector: string, attr: 'name' | 'property', key: string, value: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    setMeta('meta[name="description"]', 'name', 'description', DESCRIPTION);
    setMeta('meta[property="og:title"]', 'property', 'og:title', TITLE);
    setMeta('meta[property="og:description"]', 'property', 'og:description', DESCRIPTION);
    setMeta('meta[property="og:url"]', 'property', 'og:url', CANONICAL);
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }

  return (
    <div className="min-h-screen bg-cream text-ink font-grotesk antialiased">
      <SiteHeader />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <main className="pt-28 pb-24">
        <article className="max-w-content mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-charcoal/70 hover:text-ink mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <header className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.18em] text-charcoal/60 mb-4">
              Guide · Organizational culture
            </p>
            <h1 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
              How to Measure Organizational Culture: A Guide for Mid-Market Leaders
            </h1>
            <p className="mt-6 text-lg text-charcoal/85 leading-relaxed">
              Most leaders can feel their organizational culture shifting long before a survey
              picks it up. This guide is for the moment after the informal signals stop reaching
              you — when the company is too big for hallway conversation and too small for a
              full engagement machine — and you need a real way to measure what's actually
              happening.
            </p>
          </header>

          <div className="prose-content mt-12 space-y-14 max-w-3xl">
            <Section id="what-is-culture" title="What organizational culture actually is">
              <p>
                Organizational culture is the pattern of behaviors, decisions, and unwritten
                rules that shape how work gets done when no one is watching. It is not the
                values statement on the wall, and it is not the score on an annual engagement
                survey. It is the gap — or the alignment — between what a company says it
                stands for and what its people experience day to day.
              </p>
              <p>
                For mid-market and PE-backed companies, that gap matters more than in either
                early-stage startups or large enterprises. You are past the point where the
                founder can hold culture in their head, and before the point where a large HR
                function can absorb the drift. Measurement has to be lightweight enough to
                sustain and honest enough to act on.
              </p>
            </Section>

            <Section id="stated-vs-lived" title="Stated vs. lived culture: the gap that matters">
              <p>
                Every measurement approach eventually has to answer the same question: are we
                measuring what leaders say the culture is, or what employees actually experience?
                We call this the <strong>stated vs. lived</strong> gap, and it is the single
                most useful lens for measuring organizational culture.
              </p>
              <ul>
                <li>
                  <strong>Stated culture</strong> is the intended culture — values, principles,
                  leadership behaviors the company has committed to.
                </li>
                <li>
                  <strong>Lived culture</strong> is what employees would say when they are sure
                  no one is listening — how decisions really get made, who really gets
                  promoted, what is really rewarded and punished.
                </li>
              </ul>
              <p>
                A healthy culture is not one where the gap is zero. A healthy culture is one
                where the gap is visible, small enough to close, and moving in the right
                direction. The job of measurement is to make that gap legible.
              </p>
            </Section>

            <Section id="why-surveys-fall-short" title="Why generic engagement surveys fall short">
              <p>
                Annual engagement surveys were designed for a different era. They tell you a
                score, but not why. They surface averages, but hide the small, growing tensions
                that predict regretted departures. And they cost your people a large block of
                time only to give leaders a PDF they can't act on for another quarter.
              </p>
              <p>Three specific problems compound in the mid-market:</p>
              <ol>
                <li>
                  <strong>Aggregation hides the signal.</strong> A composite engagement score of
                  72 tells you nothing about whether trust in leadership is quietly collapsing
                  in one function.
                </li>
                <li>
                  <strong>The wrong unit of analysis.</strong> Numeric Likert scales flatten
                  what employees actually want to say. Themes, not scores, are how culture is
                  really described.
                </li>
                <li>
                  <strong>Response fatigue.</strong> By the third year, honest answers dry up.
                  People learn what to say and what will be filtered.
                </li>
              </ol>
            </Section>

            <Section id="framework" title="A practical framework for measuring organizational culture">
              <p>
                A modern measurement approach for organizational culture rests on four moves.
                None of them require a new HR team; all of them require some discipline about
                what you actually collect and how you protect it.
              </p>
              <h3 className="font-display text-2xl mt-8">1. Define a small number of anchors</h3>
              <p>
                Anchors are the specific dimensions of culture you care about — trust in
                leadership, clarity of direction, safety to speak up, quality of feedback,
                fairness of recognition. Six to eight is usually enough. Any more and you'll
                dilute the signal; any fewer and you'll miss the shape of the culture.
              </p>
              <h3 className="font-display text-2xl mt-8">2. Pulse continuously, not annually</h3>
              <p>
                Short, frequent pulses (one to three questions, weekly or biweekly) beat an
                annual survey on almost every dimension: response rates, honesty, ability to
                see trends, ability to act while the signal is fresh.
              </p>
              <h3 className="font-display text-2xl mt-8">3. Capture themes, not just scores</h3>
              <p>
                Numbers tell you <em>whether</em> something is changing. Themes tell you{' '}
                <em>what</em> is changing and <em>why</em>. Confidential, AI-assisted
                conversations turn what employees actually want to say into themes leaders can
                bring to the executive table — without breaking individual privacy.
              </p>
              <h3 className="font-display text-2xl mt-8">4. Protect anonymity by construction</h3>
              <p>
                Culture measurement only works if people trust that individual answers cannot
                be traced back to them. Enforce a minimum reporting threshold (typically k ≥ 5),
                keep raw responses out of leader-facing views, and be explicit about what
                leaders can and cannot see. Anonymity as a policy is fragile; anonymity built
                into the data model is durable.
              </p>
            </Section>

            <Section id="metrics" title="Metrics that actually predict cultural drift">
              <p>
                Once you have anchors and a pulse rhythm, a small set of derived metrics does
                most of the real work:
              </p>
              <ul>
                <li>
                  <strong>Anchor trend.</strong> The direction, not the absolute score, is the
                  signal. A trust anchor that has quietly moved down for six weeks is a bigger
                  story than any single quarter's number.
                </li>
                <li>
                  <strong>Biggest movers.</strong> The two or three anchors changing fastest,
                  up or down, are where leadership attention pays off.
                </li>
                <li>
                  <strong>Segment cuts.</strong> The same anchor cut by function, tenure, or
                  location tells you whether a shift is company-wide or localized — and
                  therefore whether it needs a company response or a team-level one.
                </li>
                <li>
                  <strong>Stated vs. lived divergence.</strong> Compare what leadership says
                  the culture is with the themes coming out of confidential conversations.
                  Sustained divergence is the leading indicator of regretted attrition.
                </li>
              </ul>
            </Section>

            <Section id="scaling" title="Scaling culture past the 'informal signals' phase">
              <p>
                The specific moment CultureLens was built for is the one many mid-market
                leaders describe the same way: <em>"I used to know how the company felt just
                by walking the floor, and I don't anymore."</em> That is not a failure of
                leadership. It is a structural feature of scale. Past a few hundred people,
                the informal signals that used to carry the truth about culture stop reaching
                the top.
              </p>
              <p>
                Measuring organizational culture at that point is less about running a bigger
                survey and more about installing a small, honest, continuous read that
                replaces what walking the floor used to do. The goal is not a dashboard. The
                goal is being able to act on a real cultural shift while it is still small
                enough to move.
              </p>
            </Section>

            <Section id="how-culturelens-helps" title="How CultureLens fits in">
              <p>
                CultureLens is the measurement layer built for exactly this stage. Employees
                respond to short pulses and, when they want to, have a private, AI-guided
                conversation about what's really on their mind. Leaders see anchors, themes,
                and the stated vs. lived gap — never individual responses, and never below
                the anonymity threshold.
              </p>
              <p>
                It is designed for SME and mid-market leaders, PE-backed portfolio companies,
                CHROs who need evidence rather than anecdotes, and integration teams working
                through a merger. If any of those sound like where you are, we would rather
                show you than describe it.
              </p>
            </Section>

            <div className="rounded-2xl border border-hairline bg-cream-warm/60 p-8 mt-16">
              <h2 className="font-display text-2xl">See what your culture is actually telling you</h2>
              <p className="mt-3 text-charcoal/85">
                Request access and we'll walk you through how CultureLens measures
                organizational culture in a live mid-market environment — including the
                anonymity model, the anchor set, and the leader view.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <GetStartedDialog>
                  <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full px-6 h-11">
                    Request access
                  </Button>
                </GetStartedDialog>
                <Button asChild variant="ghost" className="rounded-full">
                  <Link to="/who-we-are#contact" className="inline-flex items-center gap-2">
                    Talk to an expert <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28">
      <h2 className="font-display text-3xl md:text-[32px] tracking-tight">{title}</h2>
      <div className="mt-4 space-y-4 text-[17px] leading-[1.7] text-charcoal/90 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul>li]:mb-2 [&_ol>li]:mb-2">
        {children}
      </div>
    </section>
  );
}
